/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardIcon, GearIcon, UpdateIcon } from '@/shared/ui/radix-icons';
import { PageSEO } from '@/shared/seo';
import {
  Button,
  DashboardCardsSkeleton,
  DataState,
  DataStateError,
  withAuthRedirect,
} from '@/shared/ui';
import { PageHeaderTitleRow } from '@/features/property-management/components/page-header/PageHeaderTitleRow';
import { appLayoutContext } from '@layout/app/LayoutContext';
import { SideMenuLink } from '@layout/app/side-menu/SideMenuLink';
import { useAuth } from '@context/authentication';
import { getCurrentUserRole, isOperationalOrHigher } from '@/shared/utils/permissions';
import type { DashboardScope, OverviewView } from '@/services/housien/dashboard';
import { useLostReasonLabels } from '@/features/deals/constants/lost-reasons';
import { agencyContext } from './AgenciesContext';
import {
  TILE_CATALOGUE, TILE_LIMITS, buildTiles, groupByCategory, resolveTileIds,
  type TileContext,
} from './Overview.catalogue';
import { GroupHeadingSpacer, OverviewGroup, type TileGroup } from './Overview.tile';
import { CustomizeModal } from './Overview.customize';
import { AttentionPanel } from './Overview.panel';
import { CategoryBreakdown } from './Overview.categories';
import { OverviewModeToggle } from './Overview.mode-toggle';
import { useDashboard } from './use-dashboard';
import { useDashboardPreferences } from './use-dashboard-preferences';
import { navIconFor } from '@layout/agency/agency-nav';

/**
 * The overview: a number per card, each one a way into the list behind it.
 *
 * What changed, and why: the page used to stack eight blocks of five
 * different shapes — cards, funnel bars, lists, tables — and a reader had
 * to scroll the whole thing to learn whether anything needed doing. The
 * same facts now sit above the fold as one shape, grouped by what the
 * reader intends to do with them rather than by which repository produced
 * them, and the panel on the right carries the rows that used to be three
 * separate blocks.
 *
 * Arranging the blocks one by one is still off -- the dialog was offering
 * to hide the very numbers the page exists to show -- but the preferences
 * document it wrote to is what now stores the basic/advanced choice. The
 * dialog itself remains in the tree, unreferenced.
 *
 * The number is the click target. A tile whose filtered destination does
 * not exist yet is rendered as plain text instead of linking to an
 * unfiltered list — see `Overview.tile`.
 *
 * Two views, chosen by role rather than offered as a choice.
 *
 * Owner, admin and operational get the agency's numbers, with a switch
 * between the full screen and a shortened one -- the same numbers either
 * way, never a different set. The switch is a stored preference, so it
 * survives the next visit and the next device.
 *
 * Everyone below that gets their own numbers: four tiles and the panel,
 * read from `?scope=me`, which narrows the agency snapshot to the caller.
 * There is no switch there yet -- the advanced broker screen has not been
 * designed -- so the preference is stored and simply not offered.
 *
 * The role decides the *scope*, and the server decides it again: a broker
 * asking for the agency payload by hand would still be served lists the
 * backend scopes to them. This is which screen to draw, not a guard.
 */
/**
 * How many rows the panel carries on a screen that must not scroll.
 *
 * The panel is the last thing on a stacked page, so every row past what
 * fits pushes the category legend under the fold. What fits depends on
 * what is above it: the basic screen spends two rows of tiles and can
 * afford four, the broker screen spends one and can afford six.
 *
 * Fixed numbers rather than ones measured from the viewport: a panel that
 * gains and loses a row as the window resizes draws the eye to the row
 * that vanished, and no height is right for every screen anyway -- under
 * roughly 840px of viewport the basic screen scrolls whatever it carries.
 */
const STACKED_PANEL_ROWS = { basic: 4, broker: 6 };

/**
 * AgencyDashboard component.
 */
const AgencyDashboard = () => {
  const { useStore } = appLayoutContext;
  const updateNavigationBar = useStore((state) => state.updateNavigationBar);
  const { t } = useTranslation('@pages/agencies');

  const { useStore: useAgencyStore } = agencyContext;
  const agency = useAgencyStore((state) => state.agency);
  const agencyId = agency?.id ?? '';

  const { user } = useAuth();
  const isAgencyView = isOperationalOrHigher(
    getCurrentUserRole(agency, user?.profile?.email),
  );
  const scope: DashboardScope | undefined = isAgencyView ? undefined : 'me';

  const {
    data, isLoading, isError, retry, refresh, isRefreshing,
  } = useDashboard(agencyId, undefined, scope);
  const {
    mode, setMode, tiles: chosenTiles, setTiles, saveError,
  } = useDashboardPreferences(agencyId);
  const isBasic = mode === 'basic';
  const view: OverviewView = isAgencyView ? 'agency' : 'me';
  const [customizing, setCustomizing] = useState(false);
  const lossLabels = useLostReasonLabels();

  /*
   * Six tiles do not fill a column built for fourteen, so the basic
   * screen stacks rather than splits: tiles across the page,
   * then the panel and the category card sharing the row below. Roomier
   * tiles come with it -- at full width the compact padding reads as
   * undersized.
   */
  const isRoomy = isBasic;

  useEffect(() => {
    const dashboardPath = `/app/agencies/${agencyId}/dashboard`;
    const dashboardNav = (
      <SideMenuLink label={t('dashboard.title')} linkTo={dashboardPath} />
    );
    updateNavigationBar(dashboardNav, 1);
  }, []);

  /*
   * The loss-reason vocabulary is translated in the deals namespace; the
   * backend's own "unspecified" is not part of it and gets this screen's.
   */
  const ctx: TileContext = useMemo(() => ({
    t,
    data,
    agencyId,
    lossLabel: (reason: string) =>
      (lossLabels as Record<string, string>)[reason] ?? t('dashboard.tiles.lossReasons.unspecified'),
  }), [t, data, agencyId, lossLabels]);

  const tileIds = resolveTileIds(view, mode, chosenTiles[view][mode]);
  const groups: TileGroup[] = groupByCategory(view, buildTiles(view, tileIds, ctx), (tile) => tile.id)
    .map(({ category, items }) => ({
      id: category, title: t(`dashboard.groups.${category}`), tiles: items,
    }));
  const pickerCatalogue = useMemo(
    () => TILE_CATALOGUE[view].map((entry) => ({ tile: entry.build(ctx), category: entry.category })),
    [view, ctx],
  );

  return (
    <div
      data-testid="agency-dashboard"
      className="flex flex-1 flex-col overflow-y-auto bg-(--surface-secondary) px-(--space-6) py-(--space-5) font-[var(--font-family)] text-[var(--text-primary)]"
    >
      <PageSEO title={t('pageTitle')} description="" />

      <div className="flex flex-col gap-(--space-5)">
        {/*
          * Same shell every other list page wears -- raised surface, subtle
          * border, medium shadow -- and the same title row inside it, so the
          * overview does not read as a different product from Imóveis or
          * Clientes. The lede lives in the title row's info tooltip for the
          * same reason: loose under the heading, it was the only page in the
          * app with a paragraph there.
          *
          * One row, and no "atualizado em" stamp: the payload is a snapshot
          * served for the rest of the day, and a date nobody acts on was
          * costing a second line of the fold the nine numbers need.
          */}
        <div className="flex flex-wrap items-center gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-[var(--surface-raised)] px-(--space-5) py-(--space-4) shadow-[var(--shadow-md)]">
          {/*
            * The phone gives the title its own line.
            *
            * As one row, `flex-1` shrank the heading to 88px and broke
            * "Visão Geral" over two lines beside a 165px toggle that was
            * never going to fit next to it. Full width makes the wrap
            * happen where it was already declared -- between the title and
            * the controls -- instead of inside a word.
            */}
          <div className="min-w-0 flex-1 max-md:w-full max-md:flex-none">
            <PageHeaderTitleRow
              title={t('dashboard.title')}
              icon={navIconFor('dashboard')}
              introText={t('dashboard.lede')}
              introTestIdPrefix="overview"
            />
          </div>

          <div className="flex flex-none items-center gap-(--space-2) max-md:w-full">
            <OverviewModeToggle t={t} mode={mode} onChange={setMode} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCustomizing(true)}
              data-testid="overview-customize"
              aria-label={t('dashboard.customize.start')}
              title={t('dashboard.customize.start')}
              className="h-7 w-7 shrink-0 p-0 text-[var(--text-tertiary)]"
            >
              <GearIcon width={14} height={14} />
            </Button>
            {/* Icon only: recomputing is what you reach for after reading a
                number that looks stale, not a thing you came here to do. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={refresh}
              disabled={isRefreshing}
              data-testid="overview-refresh"
              aria-label={t('dashboard.refresh')}
              title={t('dashboard.refresh')}
              className="h-7 w-7 shrink-0 p-0 text-[var(--text-tertiary)]"
            >
              <UpdateIcon
                width={14}
                height={14}
                className={isRefreshing ? 'animate-spin' : undefined}
              />
            </Button>

          </div>
        </div>

        <DataState
          isLoading={isLoading}
          isError={isError}
          isEmpty={false}
          loadingFallback={<DashboardCardsSkeleton cards={4} />}
          errorState={
            <DataStateError
              icon={<DashboardIcon width={28} height={28} className="text-[var(--text-tertiary)] opacity-50" />}
              message={t('dashboard.loadError')}
              retryLabel={t('dashboard.retry')}
              onRetry={retry}
            />
          }
        >
          {isRoomy ? (
            /*
             * The shortened screens stack instead of splitting.
             *
             * Beside the panel, six tiles or four fill a third of the
             * column and leave the rest blank -- and every attempt to
             * close that gap by stretching the tiles produces cards taller
             * than their contents, which reads worse than the gap did.
             * Full width there is nothing to line up against: the tiles
             * span the page, and the panel and the category card share the
             * row below, where the card grows to the panel's height on its
             * own.
             */
            <div
              data-testid="overview-stack"
              className="flex flex-col gap-(--space-5)"
            >
              <div className="flex min-w-0 flex-col gap-(--space-4)">
                {groups.map((group) => (
                  <OverviewGroup key={group.id} group={group} large />
                ))}
              </div>

              <div className="grid items-stretch gap-(--space-5) grid-cols-1 min-[1100px]:[grid-template-columns:2fr_1fr]">
                <AttentionPanel
                  t={t}
                  data={data}
                  agencyId={agencyId}
                  maxRows={isAgencyView
                    ? STACKED_PANEL_ROWS.basic : STACKED_PANEL_ROWS.broker}
                />
                <CategoryBreakdown
                  t={t}
                  counts={data?.category_counts}
                  total={data?.total_properties}
                  agencyId={agencyId}
                />
              </div>
            </div>
          ) : (
            /*
              * One column until the panel would have to squeeze the tiles
              * below three across, then the split the prototype specifies.
              * The panel drops under the tiles rather than beside them, which
              * is the only arrangement where no tile label wraps past two
              * lines -- the defect that broke the first prototype.
              */
            <div
              data-testid="overview-split"
              className="grid items-start gap-(--space-5) grid-cols-1 min-[1100px]:[grid-template-columns:2.15fr_1fr]"
            >
              <div className="flex min-w-0 flex-col gap-(--space-4)">
                {groups.map((group) => (
                  <OverviewGroup key={group.id} group={group} />
                ))}
              </div>

              {/*
                * `self-stretch` overrides the grid's `items-start` for this
                * column only, so it takes the row's full height instead of its
                * own content height; the category card then grows into what is
                * left (`flex-1` on its own root) and its bottom edge lands on
                * the same line as the last row of tiles beside it. Only from
                * 1100px up: stacked, there is no row to stretch to and the
                * card would just grow into the viewport.
                */}
              <div className="flex min-w-0 flex-col gap-(--space-4) min-[1100px]:self-stretch">
                {/* The panel is a card, and it lines up with the first row of
                    cards beside it -- not with the heading above them. */}
                <div className="flex min-w-0 flex-col gap-(--space-2)">
                  <GroupHeadingSpacer />
                  <AttentionPanel t={t} data={data} agencyId={agencyId} />
                </div>
                <CategoryBreakdown
                  t={t}
                  counts={data?.category_counts}
                  total={data?.total_properties}
                  agencyId={agencyId}
                />
              </div>
            </div>
          )}
        </DataState>
      </div>

      <CustomizeModal
        opened={customizing}
        onClose={() => setCustomizing(false)}
        mode={mode}
        limit={TILE_LIMITS[mode]}
        catalogue={pickerCatalogue}
        chosen={tileIds}
        onChange={(ids) => setTiles(view, mode, ids)}
        onReset={() => setTiles(view, mode, [])}
        saveFailed={saveError}
        t={t}
      />

      <div className="h-2 shrink-0" aria-hidden="true" />
    </div>
  );
};

export default withAuthRedirect(AgencyDashboard);
